import { listAnalysisRecords, type AnalysisRecord } from '@/services/authApi';
import type { V3Lead } from '@/services/enterpriseApi';
import { listClients, listFavorites, type FavoriteItem, type TargetClientItem } from '@/services/relationsApi';
import { selectDisplayV3Leads } from '@/utils/v3LeadDisplay';

export const USAGE_CHANGED = 'tax-insight-usage-changed';

export interface UsageNotice {
  id: string;
  title: string;
  desc: string;
  at: string;
  icon: string;
  to: string;
}

export function notifyUsageChanged(): void {
  window.dispatchEvent(new Event(USAGE_CHANGED));
}

export function formatNoticeTime(iso: string, now = Date.now()): string {
  const time = Date.parse(iso);
  if (!Number.isFinite(time)) return '';
  const minutes = Math.floor(Math.max(0, now - time) / 60_000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '昨天';
  if (days < 7) return `${days} 天前`;
  return new Date(time).toLocaleDateString('zh-CN');
}

function asLeadRows(value: unknown): V3Lead[] | null {
  if (!Array.isArray(value)) return null;
  return value.filter((item) => item && typeof item === 'object') as V3Lead[];
}

/**
 * 与结果页「初步线索 / 有效线索」和线索列表同一口径：
 * 只数展示子集（去掉被组合线索收编的父线索，以及同标题、同主题重复项）。
 */
function leadCount(snapshot: Record<string, unknown> | null | undefined): number | null {
  if (!snapshot) return null;
  const leads = snapshot.leads;
  if (leads && typeof leads === 'object' && !Array.isArray(leads)) {
    const active = asLeadRows((leads as { active?: unknown }).active);
    if (active) return selectDisplayV3Leads(active).length;
  }
  // 旧快照只有 leadsPreview、没有 v3 active 时，结果页也不做组合折叠，条数与预览列表一致。
  if (Array.isArray(snapshot.leadsPreview)) return snapshot.leadsPreview.length;
  return null;
}

function analysisNotice(record: AnalysisRecord): UsageNotice {
  const count = leadCount(record.resultSnapshot);
  const stage = record.stageLabel?.trim() || '初步线索已生成';
  const countText = count == null ? '' : `，共 ${count} 条线索`;
  const params = new URLSearchParams({
    q: record.companyName,
    restore: '1',
    recordId: String(record.id),
    clientId: record.clientId,
  });
  return {
    id: `analysis-${record.id}`,
    title: stage,
    desc: `${record.companyName}${countText}，可查看结果`,
    at: record.updatedAt || record.createdAt,
    icon: 'ri-checkbox-circle-line',
    to: `/single-analysis?${params.toString()}`,
  };
}

function favoriteNotice(item: FavoriteItem): UsageNotice {
  const title = item.leadTitle || item.lead?.title || '线索';
  return {
    id: `favorite-${item.id}`,
    title: '已收藏线索',
    desc: item.companyName ? `「${title}」· ${item.companyName}` : `「${title}」`,
    at: item.createdAt,
    icon: 'ri-bookmark-line',
    to: `/leads/${encodeURIComponent(item.leadId)}`,
  };
}

function clientNotice(item: TargetClientItem): UsageNotice {
  return {
    id: `client-${item.id}`,
    title: '已加入客户',
    desc: item.sourceLeadTitle
      ? `${item.companyName} · 来自「${item.sourceLeadTitle}」`
      : item.companyName,
    at: item.createdAt,
    icon: 'ri-contacts-book-2-line',
    to: '/my/clients',
  };
}

export function buildUsageNotices(
  records: AnalysisRecord[],
  favorites: FavoriteItem[],
  clients: TargetClientItem[],
  limit = 8,
): UsageNotice[] {
  return [...records.map(analysisNotice), ...favorites.map(favoriteNotice), ...clients.map(clientNotice)]
    .filter((item) => Number.isFinite(Date.parse(item.at)))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
    .slice(0, limit);
}

export async function loadUsageNotices(): Promise<UsageNotice[]> {
  const [records, favorites, clients] = await Promise.all([
    listAnalysisRecords(),
    listFavorites(1, 20),
    listClients(),
  ]);
  return buildUsageNotices(records, favorites.items, clients.items);
}

export function noticeSeenKey(userId: string): string {
  return `tax-insight-notice-seen:${userId}`;
}
